import { useUnreads } from "@talkjs/react";

/**
 * Total unread message count across the conversations the active program
 * actually exposes to the surface.
 *
 * `useUnreads()` is a TalkJS-side aggregate — it returns every
 * conversation the TalkJS user is a participant of, regardless of which
 * program scopes the current session. The chat surfaces only mount a
 * `<Chatbox>` for conversations the backend returns in
 * `talkjsData.conversations` (program-scoped, intersected with
 * `Circle.users`). Unreads in conversations outside that set have no UI
 * to be cleared from, so an unfiltered sum produces a badge that never
 * goes to zero when local circle membership and TalkJS-side
 * participation drift apart (e.g. legacy participant rows from before
 * deterministic IDs, or a removal whose upstream DELETE failed).
 *
 * Constraining the count to `allowedConversationIds` keeps the badge in
 * lockstep with what the user can actually open: the reductions sums up
 * exactly what the resolver might mount, so anything that surfaces in
 * the badge can also be cleared by the user. Callers thread the
 * conversation ids from `talkjsData.conversations` for the active
 * program; passing `null`/empty yields 0, matching the gated/loading
 * shape of the session-init payload.
 */
export function useChatUnreadCount(
  allowedConversationIds: readonly string[] | null | undefined
): number {
  const unreads = useUnreads();

  if (!allowedConversationIds || allowedConversationIds.length === 0) {
    return 0;
  }

  const allowed = new Set(allowedConversationIds);
  return (
    unreads?.reduce((sum, item) => {
      if (!allowed.has(item.conversation.id)) return sum;
      return sum + (item.unreadMessageCount || 0);
    }, 0) ?? 0
  );
}

export default useChatUnreadCount;
